import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
} from "@mui/material";
import {
  CalendarToday,
  Person,
  ArrowBack,
  Edit,
  Delete,
  Share,
} from "@mui/icons-material";
import { blogService } from "../../services/api";
import { useAuth } from "../../services/auth";
import { toast } from "react-toastify";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://backendapi.vinautochecker.com";
const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [id]);

  // Set the document title when post is loaded
  useEffect(() => {
    if (post?.title) {
      document.title = post.title;
    }
  }, [post]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await blogService.getPost(id);
      setPost(response.data.data);
    } catch (error) {
      setError("Failed to load post");
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    setDeleting(true);
    try {
      await blogService.deletePost(id);
      toast.success("Post deleted successfully");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete post");
    } finally {
      setDeleting(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.meta_description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Post not found</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }

  const isOwner = user && post.user_id === user.id;
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };
        console.log(post.featured_image.replace(/^\//, ""));
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 4 } }}>
        {/* Post Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Box>
              <Chip
                label={post.category || "Uncategorized"}
                color="primary"
                sx={{ mb: 2 }}
              />
              {post.wordpress_id && (
                <Chip
                  label="Published on WordPress"
                  color="success"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>

            <Box>
              {isOwner && (
                <>
                  <IconButton
                    onClick={() => navigate(`/edit-post/${id}`)}
                    color="primary"
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={handleDelete}
                    color="error"
                    disabled={deleting}
                  >
                    {deleting ? <CircularProgress size={24} /> : <Delete />}
                  </IconButton>
                </>
              )}
              <IconButton onClick={handleShare} color="primary">
                <Share />
              </IconButton>
            </Box>
          </Box>

          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            fontWeight="bold"
          >
            {post.title}
          </Typography>

          <Box display="flex" alignItems="center" gap={3} sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <Person sx={{ mr: 1, color: "text.secondary" }} />
              <Typography variant="body1" color="text.secondary">
                {post.user?.name || "Anonymous"}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <CalendarToday sx={{ mr: 1, color: "text.secondary" }} />
              <Typography variant="body1" color="text.secondary">
                {new Date(post.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </Box>
          </Box>
        </Box>
                
        {/* Featured Image */}
        {post.featured_image && (
          <Box sx={{ mb: 4 }}>
            <img
              src={`${API_BASE_URL}/${post.featured_image.replace(/^storage\//, 'storage/')}`}
              alt={post.title}
              style={{
                width: "100%",
                maxHeight: "300px",
                objectFit: "cover",
                borderRadius: "12px",
              }}
            />
          </Box>
        )}

        {/* Post Content
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.1rem",
              lineHeight: 1.8,
              whiteSpace: "pre-line",
            }}
          >
            {stripHtml(post.content)}
          </Typography>
        </Box> */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.1rem",
              lineHeight: 1.8,
            }}
            component="div"  // IMPORTANT: allows inner HTML
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Box>

        {/* Tags */}
        {post.tags && (
          <Box sx={{ mb: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Tags:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {post.tags.split(",").map((tag, index) => (
                <Chip
                  key={index}
                  label={tag.trim()}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* SEO Info (Visible to post owner only) */}
        {isOwner && (
          <Paper
            variant="outlined"
            sx={{ p: 3, mt: 4, bgcolor: "background.default" }}
          >
            <Typography variant="h6" gutterBottom>
              SEO Information
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Meta Title:
              </Typography>
              <Typography variant="body2">
                {post.meta_title || "Not set"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Meta Description:
              </Typography>
              <Typography variant="body2">
                {post.meta_description || "Not set"}
              </Typography>
            </Box>
          </Paper>
        )}
      </Paper>
    </Container>
  );
};

export default PostDetail;
